import {
  CreateProductGroupPermissionsResult,
  CreateProductGroupPermissionsVariables,
  createProductGroupPermissionsMutation,
} from "../../graphqlQueries/createProductGroupPermissions";
import GroupForm, { ProductGroupFormValues } from "./GroupForm";
import {
  InsertProductGroupResult,
  InsertProductGroupVariables,
  insertProductGroupMutation,
} from "../../graphqlQueries/productGroup/insertProductGroup";
import { getProductGroupsQuery } from "../../graphqlQueries/productGroup/getProductGroups";
import { AlertProps, ProductGroupProductInput, ProductGroupRole, UserRole } from "../../types";

import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import { getHasuraRoleContext } from "../../utils/functions";
import { userInfoVar } from "../../utils/ApolloCache";
import { getEditPath, getProductGroupVariables } from "../../utils/productGroupUtils";
import {
  EditProductInformation,
  ProductGroupImage,
  ProductGroupProductInformation,
  UpdateProductGroupResult,
  UpdateProductGroupVariables,
  updateProductGroupMutation,
} from "../../graphqlQueries/productGroup/updateProductGroup";

type CreateGroupFormProps = {
  rootPagePath: string;
};

const CreateGroupForm: React.FunctionComponent<CreateGroupFormProps> = ({ rootPagePath }) => {
  const apolloClient = useApolloClient();
  const history = useHistory();
  const { t } = useTranslation();
  const [alert, setAlert] = useState<AlertProps | undefined>(undefined);

  const onSubmit = async (
    formValues: ProductGroupFormValues,
    products: ProductGroupProductInput[],
    editedProductDetails?: ProductGroupProductInformation[],
    editedProductImages?: ProductGroupImage[]
  ) => {
    const addProducts = products.filter((p) => !!p.productGroupId);
    const variables = {
      productGroup: {
        description: formValues.groupInstructions,
        name: formValues.groupName,
        products: { data: addProducts },
      },
    };
    try {
      const result = await apolloClient.mutate<
        InsertProductGroupResult,
        InsertProductGroupVariables
      >({
        context: getHasuraRoleContext(UserRole.ManageProductGroup),
        mutation: insertProductGroupMutation,
        variables,
      });

      const resultData = result.data?.insertProductGroupOne;
      const productGroupId = resultData?.id;
      if (!productGroupId) {
        throw new Error("No id returned");
      }
      const userId = userInfoVar()?.userId;
      if (!userId) {
        throw new Error("No userId found");
      }

      const usersCompanyIds = userInfoVar()?.companyIds ?? [];
      const userCompanyPermissions = usersCompanyIds.map((companyId) => ({
        companyId,
        role: ProductGroupRole.Admin,
      }));

      const usersFromDb: {
        userId: string;
        role: ProductGroupRole;
      }[] =
        formValues?.emailsToInvite?.map((user) => ({
          role: ProductGroupRole.Editor,
          userId: user.value,
        })) || [];

      const organizationsFromDb: { companyId: string; role: ProductGroupRole }[] =
        formValues?.organizationsToInvite?.map((organization) => ({
          companyId: organization.value,
          role: ProductGroupRole.Editor,
        })) || [];

      const editImages = editedProductImages;

      await apolloClient.mutate<
        CreateProductGroupPermissionsResult,
        CreateProductGroupPermissionsVariables
      >({
        context: getHasuraRoleContext(UserRole.ManageProductGroup),
        mutation: createProductGroupPermissionsMutation,
        refetchQueries: [getProductGroupsQuery],
        variables: {
          companies: [...userCompanyPermissions, ...organizationsFromDb],
          productGroupId: productGroupId,
          users: [{ role: ProductGroupRole.Admin, userId }, ...usersFromDb],
        },
      });
      const createProducts = products.filter((p) => !p.productGroupId);

      if (resultData && createProducts.length > 0) {
        const variables: UpdateProductGroupVariables = getProductGroupVariables({
          editedProductDetails,
          editedProductDetailsFromDb: [],
          editedProductImages,
          editedProductImagesFromDb: [],
          groupId: productGroupId,
          productGroup: {
            description: formValues.groupInstructions,
            name: formValues.groupName,
          },
          products: createProducts.map((p) => ({ ...p, productGroupId })),
        });
        const hasEditedProductImages = editedProductImages && editedProductImages?.length > 0;
        await apolloClient.mutate<UpdateProductGroupResult, UpdateProductGroupVariables>({
          context: getHasuraRoleContext(UserRole.ManageProductGroup),
          mutation: updateProductGroupMutation(
            true,
            [],
            [],
            editedProductDetails as EditProductInformation[],
            [],
            editImages,
            [],
            true,
            hasEditedProductImages
          ),
          refetchQueries: [getProductGroupsQuery],
          variables: {
            ...variables,
            editImages: editImages || [],
            editedProductImages: editedProductImages || [],
            editedProductInformationIds: [...products].map((p) => p.productId),
            newProducts: createProducts.map((p) => ({ ...p, productGroupId })),
          },
        });
      }
      history.replace(getEditPath(rootPagePath, productGroupId), {
        alert: { text: t("common.success"), type: "success" },
      });
    } catch (err) {
      console.error(err);
      const error = err as Error;
      setAlert({ text: error.message, type: "danger" });
    }
  };

  return (
    <GroupForm
      onSubmit={onSubmit}
      rootPagePath={rootPagePath}
      productGroupRole={ProductGroupRole.Admin}
      alert={alert}
      closeAlert={() => setAlert(undefined)}
    />
  );
};

export default CreateGroupForm;
